import { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { supabase } from "../lib/supabaseClient";

const colors = {
    cream: "#fffaf2",
    paper: "rgba(255, 250, 242, 0.94)",
    white: "rgba(255,255,255,0.72)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    softMuted: "rgba(42, 38, 33, 0.48)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    brownDarker: "#543b26",
    border: "rgba(155,118,83,0.16)",
    dangerBg: "rgba(211,47,47,0.08)",
};

function formatDate(dateString) {
    if (!dateString) return "";

    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    }).format(new Date(dateString));
}

async function getPublishedNewsBySlug(slug) {
    const { data, error } = await supabase
        .from("news")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .single();

    if (error) {
        throw new Error(error.message);
    }

    return data;
}

function NewsDetail() {
    const { slug } = useParams();

    const [newsItem, setNewsItem] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        async function loadNewsItem() {
            try {
                setIsLoading(true);
                setErrorMessage("");

                const data = await getPublishedNewsBySlug(slug);
                setNewsItem(data);
            } catch (error) {
                console.error(error);
                setErrorMessage("Der Beitrag konnte nicht gefunden werden.");
            } finally {
                setIsLoading(false);
            }
        }

        loadNewsItem();
    }, [slug]);

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100vh",
                position: "relative",
                overflow: "hidden",
                py: {
                    xs: 9,
                    md: 13,
                },
                background:
                    "radial-gradient(circle at 14% 18%, rgba(155,118,83,0.16) 0%, rgba(155,118,83,0) 30%), radial-gradient(circle at 88% 8%, rgba(29,118,189,0.1) 0%, rgba(29,118,189,0) 28%), linear-gradient(180deg, #fffaf2 0%, #f7f1e8 55%, #eef7fa 100%)",
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    position: "absolute",
                    width: 360,
                    height: 360,
                    borderRadius: "50%",
                    backgroundColor: "rgba(155,118,83,0.1)",
                    filter: "blur(18px)",
                    top: -150,
                    right: -130,
                }}
            />

            <Box
                aria-hidden="true"
                sx={{
                    position: "absolute",
                    width: 260,
                    height: 260,
                    borderRadius: "50%",
                    backgroundColor: "rgba(238,247,250,0.92)",
                    filter: "blur(14px)",
                    bottom: -100,
                    left: -80,
                }}
            />

            <Container
                maxWidth={false}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    maxWidth: 1120,
                    mx: "auto",
                    px: {
                        xs: 2,
                        sm: 3,
                        md: 4,
                    },
                }}
            >
                {isLoading ? (
                    <Paper
                        elevation={0}
                        sx={{
                            py: 9,
                            px: 3,
                            borderRadius: 5,
                            backgroundColor: colors.paper,
                            border: `1px solid ${colors.border}`,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <CircularProgress sx={{ color: colors.brownDark }} />

                        <Typography
                            sx={{
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 15,
                                fontWeight: 800,
                                color: colors.muted,
                            }}
                        >
                            Beitrag wird geladen...
                        </Typography>
                    </Paper>
                ) : errorMessage ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: {
                                xs: 3,
                                md: 5,
                            },
                            borderRadius: 5,
                            backgroundColor: colors.paper,
                            border: `1px solid ${colors.border}`,
                            boxShadow: "0 24px 80px rgba(78,54,30,0.09)",
                        }}
                    >
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 3,
                                backgroundColor: colors.dangerBg,
                                fontFamily: '"Nunito Sans", sans-serif',
                                "& .MuiAlert-message": {
                                    fontWeight: 700,
                                },
                            }}
                        >
                            {errorMessage}
                        </Alert>

                        <Typography
                            component="h1"
                            sx={{
                                mb: 1.5,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "2.2rem",
                                    md: "3rem",
                                },
                                lineHeight: 1,
                                fontWeight: 800,
                                letterSpacing: "-0.055em",
                                color: colors.ink,
                            }}
                        >
                            Beitrag nicht verfügbar.
                        </Typography>

                        <Typography
                            sx={{
                                mb: 3,
                                maxWidth: 620,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 16,
                                lineHeight: 1.7,
                                color: colors.muted,
                            }}
                        >
                            Der Beitrag wurde möglicherweise entfernt, ist noch nicht
                            veröffentlicht oder der Link ist nicht korrekt.
                        </Typography>

                        <Button
                            component={RouterLink}
                            to="/"
                            variant="contained"
                            sx={{
                                minHeight: 52,
                                px: 3.5,
                                borderRadius: 999,
                                backgroundColor: colors.brownDark,
                                color: "#fff",
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                boxShadow: "0 14px 30px rgba(111,81,54,0.22)",

                                "&:hover": {
                                    backgroundColor: colors.brownDarker,
                                    boxShadow:
                                        "0 18px 38px rgba(111,81,54,0.28)",
                                },
                            }}
                        >
                            Zur Startseite
                        </Button>
                    </Paper>
                ) : (
                    <Box component="article">
                        <Paper
                            elevation={0}
                            sx={{
                                position: "relative",
                                overflow: "hidden",
                                borderRadius: {
                                    xs: 5,
                                    md: 7,
                                },
                                backgroundColor: colors.paper,
                                border: `1px solid ${colors.border}`,
                                boxShadow:
                                    "0 30px 90px rgba(78,54,30,0.13), 0 2px 12px rgba(78,54,30,0.05)",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    minHeight: newsItem.image_url
                                        ? {
                                            xs: 380,
                                            sm: 460,
                                            md: 620,
                                        }
                                        : "auto",
                                    display: "flex",
                                    alignItems: newsItem.image_url
                                        ? "flex-end"
                                        : "center",
                                    background: newsItem.image_url
                                        ? `linear-gradient(180deg, rgba(42,38,33,0.12) 0%, rgba(42,38,33,0.28) 42%, rgba(42,38,33,0.72) 100%), url(${newsItem.image_url})`
                                        : "radial-gradient(circle at 88% 14%, rgba(155,118,83,0.18) 0%, rgba(155,118,83,0) 30%), linear-gradient(145deg, rgba(255,250,242,0.96) 0%, rgba(247,241,232,0.92) 100%)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        p: {
                                            xs: 3,
                                            sm: 4,
                                            md: 6,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            maxWidth: 860,
                                            color: newsItem.image_url
                                                ? "#fff"
                                                : colors.ink,
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            flexWrap="wrap"
                                            gap={1}
                                            sx={{ mb: 2 }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    minHeight: 34,
                                                    px: 1.8,
                                                    borderRadius: 999,
                                                    backgroundColor:
                                                        newsItem.image_url
                                                            ? "rgba(255,255,255,0.18)"
                                                            : "rgba(155,118,83,0.12)",
                                                    backdropFilter: "blur(12px)",
                                                    border: newsItem.image_url
                                                        ? "1px solid rgba(255,255,255,0.24)"
                                                        : `1px solid ${colors.border}`,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily:
                                                            '"Nunito Sans", sans-serif',
                                                        fontSize: 12,
                                                        fontWeight: 900,
                                                        letterSpacing: "0.14em",
                                                        textTransform:
                                                            "uppercase",
                                                        color: newsItem.image_url
                                                            ? "#fff"
                                                            : colors.brownDark,
                                                    }}
                                                >
                                                    Aktuelles
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    minHeight: 34,
                                                    px: 1.8,
                                                    borderRadius: 999,
                                                    backgroundColor:
                                                        newsItem.image_url
                                                            ? "rgba(255,255,255,0.18)"
                                                            : colors.white,
                                                    backdropFilter: "blur(12px)",
                                                    border: newsItem.image_url
                                                        ? "1px solid rgba(255,255,255,0.24)"
                                                        : `1px solid ${colors.border}`,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily:
                                                            '"Nunito Sans", sans-serif',
                                                        fontSize: 12,
                                                        fontWeight: 900,
                                                        color: newsItem.image_url
                                                            ? "#fff"
                                                            : colors.softMuted,
                                                    }}
                                                >
                                                    {formatDate(
                                                        newsItem.published_at ||
                                                        newsItem.created_at
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Typography
                                            component="h1"
                                            sx={{
                                                maxWidth: 880,
                                                fontFamily:
                                                    '"Quicksand", sans-serif',
                                                fontSize: {
                                                    xs: "2.45rem",
                                                    sm: "3.4rem",
                                                    md: "4.9rem",
                                                },
                                                lineHeight: 0.94,
                                                fontWeight: 800,
                                                letterSpacing: "-0.065em",
                                                textShadow: newsItem.image_url
                                                    ? "0 12px 34px rgba(0,0,0,0.26)"
                                                    : "none",
                                            }}
                                        >
                                            {newsItem.title}
                                        </Typography>

                                        {newsItem.excerpt && (
                                            <Typography
                                                sx={{
                                                    mt: 2.4,
                                                    maxWidth: 760,
                                                    fontFamily:
                                                        '"Nunito Sans", sans-serif',
                                                    fontSize: {
                                                        xs: 18,
                                                        md: 22,
                                                    },
                                                    lineHeight: 1.55,
                                                    fontWeight: 800,
                                                    color: newsItem.image_url
                                                        ? "rgba(255,255,255,0.9)"
                                                        : colors.brownDark,
                                                    textShadow:
                                                        newsItem.image_url
                                                            ? "0 10px 26px rgba(0,0,0,0.22)"
                                                            : "none",
                                                }}
                                            >
                                                {newsItem.excerpt}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    p: {
                                        xs: 3,
                                        sm: 4,
                                        md: 6,
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            md: "180px 1fr",
                                        },
                                        gap: {
                                            xs: 3,
                                            md: 5,
                                        },
                                        alignItems: "start",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: {
                                                md: "sticky",
                                            },
                                            top: {
                                                md: 96,
                                            },
                                        }}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2.2,
                                                borderRadius: 4,
                                                backgroundColor:
                                                    "rgba(255,255,255,0.58)",
                                                border: `1px solid ${colors.border}`,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    mb: 0.7,
                                                    fontFamily:
                                                        '"Nunito Sans", sans-serif',
                                                    fontSize: 12,
                                                    fontWeight: 900,
                                                    letterSpacing: "0.14em",
                                                    textTransform: "uppercase",
                                                    color: colors.brown,
                                                }}
                                            >
                                                Veröffentlicht
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        '"Quicksand", sans-serif',
                                                    fontSize: "1.35rem",
                                                    lineHeight: 1.1,
                                                    fontWeight: 800,
                                                    letterSpacing: "-0.04em",
                                                    color: colors.ink,
                                                }}
                                            >
                                                {formatDate(
                                                    newsItem.published_at ||
                                                    newsItem.created_at
                                                )}
                                            </Typography>
                                        </Paper>
                                    </Box>

                                    <Box>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontFamily:
                                                    '"Nunito Sans", sans-serif',
                                                fontSize: {
                                                    xs: 17,
                                                    md: 18.5,
                                                },
                                                lineHeight: 1.9,
                                                color: colors.muted,
                                                whiteSpace: "pre-line",

                                                "& p": {
                                                    mb: 2,
                                                },
                                            }}
                                        >
                                            {newsItem.content ||
                                                "Für diesen Beitrag wurde noch kein Inhalt hinterlegt."}
                                        </Typography>

                                        <Divider
                                            sx={{
                                                my: {
                                                    xs: 4,
                                                    md: 5,
                                                },
                                                borderColor:
                                                    "rgba(42,38,33,0.1)",
                                            }}
                                        />

                                        <Stack
                                            direction={{
                                                xs: "column",
                                                sm: "row",
                                            }}
                                            spacing={1.5}
                                            justifyContent="space-between"
                                            alignItems={{
                                                xs: "stretch",
                                                sm: "center",
                                            }}
                                        >
                                            <Button
                                                component={RouterLink}
                                                to="/"
                                                variant="outlined"
                                                sx={{
                                                    minHeight: 52,
                                                    px: 3.5,
                                                    borderRadius: 999,
                                                    borderWidth: 2,
                                                    borderColor: colors.brown,
                                                    color: colors.brownDark,
                                                    fontFamily:
                                                        '"Nunito Sans", sans-serif',
                                                    fontSize: 14,
                                                    fontWeight: 900,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.05em",

                                                    "&:hover": {
                                                        borderWidth: 2,
                                                        borderColor:
                                                        colors.brownDark,
                                                        backgroundColor:
                                                            "rgba(155,118,83,0.08)",
                                                    },
                                                }}
                                            >
                                                Zurück zur Startseite
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Container>
        </Box>
    );
}

export default NewsDetail;